/**
 * @file index.tsx
 *
 * @fileoverview Renders a resume teaser.
 */
import React, { useContext, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
  ResumeBookmark,
  ResumeBookmarkProps,
} from '~/jobiqo/components/BookmarkedResumes/components/Bookmark';
import ResumeBookmarkPlacement from '~/jobiqo/components/BookmarkedResumes/enums/ResumeBookmarkPlacement';
import { ContactRequest } from '~/jobiqo/components/Resume/ContactRequest';
import ResumeLink from '~/jobiqo/components/Resume/Link';
import { getResumeTitle } from '~/jobiqo/components/Resume/utils';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { isRecruiter } from '~/jobiqo/lib/auth';
import { Resume, TermLevel } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { BriefcaseIcon } from '~/vcl/icons/Briefcase';
import { CalendarIcon } from '~/vcl/icons/Calendar';
import { EnvelopeIcon } from '~/vcl/icons/Envelope';
import { MapMarkerIcon } from '~/vcl/icons/MapMarker';
import { PaperIcon } from '~/vcl/icons/Paper';
import { StarLineIcon } from '~/vcl/icons/StarLine';
import { ResumeTeaser as ResumeTeaserCard } from '~/vcl/organisms/08-resume/02-resume-teaser';
import { parseUniversalDate } from '~/jobiqo/utils/dates';
import MatchingScore from '~/jobiqo/components/ResumeMatching/components/MatchingScore';
import { Badge } from '~/vcl/atoms/03-badge';
import { ResumeTeaserExtraInfoSlot } from '~/jobiqo/components/Slots/ResumeTeaserExtraInfoSlot';
import { Expander, ExpanderTrigger } from '~/vcl/atoms/22-expander';
import styles from './resumeTeaser.module.scss';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import ResumeTeaserBadges from '~/jobiqo/components/Resume/Teaser/components/Badges';
const MAX_VISIBLE_SKILLS = 3;

/**
 * Renders a list of skills with an expandable "+ N others" toggle.
 * Initially shows top 3 skills; clicking the toggle reveals the full list.
 */
function SkillsList({ skills }: { skills: Array<TermLevel | null> }) {
  const [expanded, setExpanded] = useState(false);
  const labels = skills.map((s) => s?.term?.label || s?.label).filter(Boolean);
  const hasMore = labels.length > MAX_VISIBLE_SKILLS;
  const visibleLabels = expanded ? labels : labels.slice(0, MAX_VISIBLE_SKILLS);
  const remainingCount = labels.length - MAX_VISIBLE_SKILLS;

  if (!hasMore) {
    return <>{labels.join(', ')}</>;
  }

  return (
    <Expander isOpen={expanded} onChange={() => setExpanded(!expanded)}>
      {visibleLabels.join(', ')}
      {!expanded && (
        <>
          {' '}
          <ExpanderTrigger>
            <Text tag="span" color="primary">
              <FormattedMessage
                id="resume_teaser_skills_more"
                defaultMessage="+ {count} others"
                values={{ count: remainingCount }}
              />
            </Text>
          </ExpanderTrigger>
        </>
      )}
    </Expander>
  );
}

export interface ResumeTeaserProps
  extends Pick<ResumeBookmarkProps, 'bookmarkMutationCallback'> {
  /**
   * The resume (use an actual type)
   */
  resume: Resume;
  /**
   * If contact requests need approval from applicants.
   *
   * @default true
   */
  needsApproval?: boolean;
  /**
   * The matching score if transparency mode is enabled.
   */
  matchingScore?: number;
}

export function ResumeTeaser({
  resume,
  bookmarkMutationCallback,
  needsApproval = true,
  matchingScore,
}: ResumeTeaserProps) {
  const userContext: UserContextInterface = useContext(UserContext);
  const { JOBS } = useFrontendConfig();
  const SEASONAL_JOBS_ENABLED = JOBS.SEASONAL_JOBS_ENABLED;
  const isUserRecruiter = userContext.user && isRecruiter(userContext.user);
  const intl = useIntl();

  return (
    <ResumeTeaserCard
      profileHeader={
        <>
          <FlexItems
            alignItems="center"
            gap={[0, 2]}
            flexWrap={['wrap', 'nowrap']}
          >
            <ResumeLink resume={resume}>{resume.title}</ResumeLink>
            {resume.homeLocation && (
              <FlexItems
                alignItems="center"
                gap={1}
                flexWrap="nowrap"
                className={styles.homeLocation}
              >
                <MapMarkerIcon
                  title={intl.formatMessage({
                    id: 'resume_teaser_home_location_icon_title',
                    defaultMessage: "Candidate's current location",
                  })}
                  color="gray4"
                />
                <Text color="gray4" margin="0" fontSize={3} marginLeft={4}>
                  {resume.homeLocation}
                </Text>
              </FlexItems>
            )}
          </FlexItems>
          {resume.privateFieldsAccess && (
            <Text color="gray4" margin="0" fontSize={3}>
              {getResumeTitle(resume)}
            </Text>
          )}
        </>
      }
      name={resume.title ?? ''}
      {...(resume.privateFieldsAccess && resume.picture
        ? { picture: resume.picture.url }
        : !resume.privateFieldsAccess
        ? { picture: '/static/images/lock_pic.jpg' }
        : null)}
      {...(isUserRecruiter
        ? {
            actions: (
              <FlexItems alignItems="center" gap={4}>
                {isUserRecruiter && (
                  <>
                    <FlexItem>
                      <MatchingScore matchingScore={matchingScore} />
                    </FlexItem>
                    <FlexItem>
                      <ResumeBookmark
                        placement={ResumeBookmarkPlacement.RESUME_TEASER}
                        resume={resume}
                        bookmarkMutationCallback={bookmarkMutationCallback}
                      />
                    </FlexItem>
                  </>
                )}
                <FlexItem>
                  <ContactRequest
                    resume={resume}
                    needsApproval={needsApproval}
                  />
                </FlexItem>
              </FlexItems>
            ),
          }
        : null)}
      fields={
        <>
          {resume?.hasAttachment && (
            <div>
              <Badge badgeStyle="primaryVeryLight">
                <FlexItems
                  flexWrap="nowrap"
                  alignItems="center"
                  className="jd-resume-teaser-has-attachment"
                >
                  <PaperIcon
                    height="15"
                    width="15"
                    color="gray4"
                    title={intl.formatMessage({
                      id: 'resume_teaser_has_attachment_icon_title',
                      defaultMessage:
                        'This resume has an attached document (e.g. CV, cover letter)',
                    })}
                  />
                  <Text color="gray4" margin="0" marginLeft={4}>
                    <FormattedMessage
                      id="resume_teaser_has_attachment_title"
                      defaultMessage="Attachment available"
                    />
                  </Text>
                </FlexItems>
              </Badge>
              <Spacing />
            </div>
          )}
          {resume.privateFieldsAccess && (
            <>
              <FlexItems
                flexWrap="nowrap"
                alignItems="center"
                className="jd-resume-teaser-email"
              >
                <EnvelopeIcon
                  height="15"
                  width="15"
                  title="Search"
                  color="gray4"
                />
                <Text color="gray4" margin="0" marginLeft={4}>
                  {resume.email}
                </Text>
              </FlexItems>
              <Spacing />
            </>
          )}
          <FlexItems
            flexWrap="nowrap"
            alignItems="center"
            className="jd-resume-teaser-desired-location"
          >
            <MapMarkerIcon
              height="15"
              width="15"
              title={intl.formatMessage({
                id: 'resume_teaser_preferred_location_icon_title',
                defaultMessage: "Candidate's preferred work location",
              })}
              color="gray4"
            />
            <Text color="gray4" margin="0" marginLeft={4}>
              <FormattedMessage
                id="resume_teaser_preferred_location_label"
                defaultMessage="Preferred Location:"
              />{' '}
              {resume.desiredLocation?.length ? (
                resume.desiredLocation.join(' | ')
              ) : (
                <FormattedMessage
                  id="resume_teaser_job_seeker_no_region"
                  defaultMessage="No desired location"
                />
              )}
            </Text>
          </FlexItems>
          <Spacing />
          {/* Joined on - resume creation date */}
          {(resume as any).created && (
            <>
              <FlexItems
                flexWrap="nowrap"
                alignItems="center"
                className="jd-resume-teaser-joined-on"
              >
                <CalendarIcon
                  height="15"
                  width="15"
                  title={intl.formatMessage({
                    id: 'resume_teaser_joined_on_icon_title',
                    defaultMessage:
                      'Date when the candidate created their resume',
                  })}
                  color="gray4"
                />
                <Text color="gray4" margin="0" marginLeft={4}>
                  <FormattedMessage
                    id="resume_teaser_joined_on_label"
                    defaultMessage="Joined on:"
                  />{' '}
                  <FormattedDate
                    value={parseUniversalDate((resume as any).created)}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />
                </Text>
              </FlexItems>
              <Spacing />
            </>
          )}
          {/* Updated on - resume last modified date */}
          {(resume as any).changed && (
            <>
              <FlexItems
                flexWrap="nowrap"
                alignItems="center"
                className="jd-resume-teaser-updated-on"
              >
                <CalendarIcon
                  height="15"
                  width="15"
                  title={intl.formatMessage({
                    id: 'resume_teaser_updated_on_icon_title',
                    defaultMessage: 'Date when the resume was last updated',
                  })}
                  color="gray4"
                />
                <Text color="gray4" margin="0" marginLeft={4}>
                  <FormattedMessage
                    id="resume_teaser_updated_on_label"
                    defaultMessage="Updated on:"
                  />{' '}
                  <FormattedDate
                    value={parseUniversalDate((resume as any).changed)}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />
                </Text>
              </FlexItems>
              <Spacing />
            </>
          )}
          {/* Skills - top 3 with expandable "+ N others" toggle */}
          {resume.skills && resume.skills.length > 0 && (
            <>
              <FlexItems
                flexWrap="nowrap"
                alignItems="center"
                className="jd-resume-teaser-skills"
              >
                <StarLineIcon
                  height="15"
                  width="15"
                  title={intl.formatMessage({
                    id: 'resume_teaser_skills_icon_title',
                    defaultMessage: "Candidate's skills",
                  })}
                  color="gray4"
                  style={{ flexShrink: 0 }}
                />
                <Text color="gray4" margin="0" marginLeft={4}>
                  <SkillsList skills={resume.skills} />
                </Text>
              </FlexItems>
              <Spacing />
            </>
          )}
          <FlexItems
            flexWrap="nowrap"
            alignItems="center"
            className="jd-resume-teaser-availability"
          >
            <CalendarIcon
              height="15"
              width="15"
              title="Availability"
              color="gray4"
            />
            <Text color="gray4" margin="0" marginLeft={4}>
              {resume.careerStatus ? (
                resume.careerStatus.label
              ) : (
                <FormattedMessage
                  id="resume_teaser_job_seeker_no_status"
                  defaultMessage="Not seeking"
                />
              )}
              {', '}
              {resume.availableStart ? (
                <FormattedMessage
                  id="resume_teaser_job_seeker_availability_from"
                  defaultMessage="available {available}"
                  values={{
                    available: (
                      <FormattedDate
                        value={parseUniversalDate(resume.availableStart)}
                      />
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  id="resume_teaser_job_seeker_no_availability"
                  defaultMessage="no availability date"
                />
              )}
            </Text>
          </FlexItems>
          <Spacing />
          {SEASONAL_JOBS_ENABLED &&
            (resume as any).availabilityPeriods &&
            (resume as any).availabilityPeriods.length > 0 &&
            (resume as any).availabilityPeriods.map(
              (period, index) =>
                period &&
                period.startDate &&
                period.endDate && (
                  <FlexItems
                    key={index}
                    flexWrap="nowrap"
                    alignItems="center"
                    className="jd-resume-teaser-availability-period"
                  >
                    <CalendarIcon
                      height="15"
                      width="15"
                      title={intl.formatMessage({
                        id: 'resume_teaser_availability_period_icon_title',
                        defaultMessage: 'Available between',
                      })}
                      color="gray4"
                    />
                    <Text color="gray4" margin="0" marginLeft={4}>
                      <FormattedMessage
                        id="resume_teaser_job_seeker_availability_period"
                        defaultMessage="Available between: {start} - {end}"
                        values={{
                          start: (
                            <FormattedDate
                              value={parseUniversalDate(period.startDate)}
                            />
                          ),
                          end: (
                            <FormattedDate
                              value={parseUniversalDate(period.endDate)}
                            />
                          ),
                        }}
                      />
                    </Text>
                  </FlexItems>
                ),
            )}
          <Spacing />
          {resume.privateFieldsAccess && (
            <>
              <FlexItems
                flexWrap="nowrap"
                alignItems="center"
                className="jd-resume-teaser-last-employer"
              >
                <BriefcaseIcon
                  height="15"
                  width="15"
                  title={intl.formatMessage({
                    id: 'resume_teaser_last_employer_icon_title',
                    defaultMessage: "Candidate's last or current employer",
                  })}
                  color="gray4"
                />
                <Text color="gray4" margin="0" marginLeft={4}>
                  {resume.workXP && resume.workXP.length > 0 ? (
                    resume.workXP[resume.workXP.length - 1]?.timePeriod
                      ?.startDate ===
                    resume.workXP[resume.workXP.length - 1]?.timePeriod
                      ?.endDate ? (
                      <FormattedMessage
                        id="resume_teaser_empty_current_employment"
                        defaultMessage="Currently working at : {lastemployer}"
                        values={{
                          lastemployer:
                            resume.workXP[resume.workXP.length - 1]
                              ?.companyName,
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="resume_teaser_empty_last_employment"
                        defaultMessage="Last employer : {lastemployer}"
                        values={{
                          lastemployer: resume.workXP[0]?.companyName,
                        }}
                      />
                    )
                  ) : (
                    <FormattedMessage
                      id="resume_teaser_empty_no_last_employment"
                      defaultMessage="No last employer"
                    />
                  )}
                </Text>
              </FlexItems>
              <Spacing />
            </>
          )}
          {resume?.['badges'] && resume?.['badges'].length > 0 && (
            <>
              <Spacing />
              <ResumeTeaserBadges badges={resume['badges']} />
            </>
          )}
          <ResumeTeaserExtraInfoSlot resume={resume} />
          <Spacing />
        </>
      }
    />
  );
}
export default ResumeTeaser;
